import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Fade,
  Slide,
  Grow,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  Send,
  ArrowBack
} from '@mui/icons-material';

const ContactPage = ({ onBack }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setSuccess(false), 5000);
      } else {
        setError(data.error || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    {
      icon: Email,
      title: 'Email',
      content: 'pathly4u@gmail.com',
      description: 'Send us an email anytime'
    },
    {
      icon: Phone,
      title: 'Phone',
      content: '650-316-0478',
      description: 'Mon-Fri from 8am to 6pm'
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      pt: 8
    }}>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Header */}
        <Fade in timeout={800}>
          <Box textAlign="center" mb={8}>
            <Typography 
              variant="h2" 
              sx={{ 
                color: 'white', 
                fontWeight: 700,
                mb: 3,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                fontSize: { xs: '2.5rem', md: '3.5rem' }
              }}
            >
              Get in Touch
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                color: 'rgba(255,255,255,0.9)', 
                fontWeight: 300,
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </Typography>
          </Box>
        </Fade>

        <Grid container spacing={6}>
          {/* Contact Information ONLY (remove contact form) */}
          <Grid item xs={12} md={12}>
            <Slide direction="up" in timeout={1200}>
              <Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 600, 
                    mb: 4,
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}
                >
                  Contact Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
                  {contactInfo.map((info, index) => (
                    <Grow in timeout={1400 + index * 200} key={index}>
                      <Card sx={{ 
                        background: 'rgba(255,255,255,0.95)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: 3,
                        minWidth: 320,
                        maxWidth: 400
                      }}>
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box sx={{ 
                              color: 'primary.main',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              <info.icon sx={{ fontSize: 28 }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {info.title}
                            </Typography>
                          </Box>
                          <Typography variant="h6" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                            {info.content}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {info.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grow>
                  ))}
                </Box>
              </Box>
            </Slide>
          </Grid>
        </Grid>

        {/* Back Button */}
        <Box textAlign="center" mt={8}>
          <Slide direction="up" in timeout={1600}>
            <Box 
              onClick={onBack}
              sx={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                color: 'white',
                padding: 2,
                borderRadius: 3,
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'rgba(255,255,255,0.2)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <ArrowBack />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Back to Home
              </Typography>
            </Box>
          </Slide>
        </Box>
      </Container>
    </Box>
  );
};

export default ContactPage; 